import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import NavbarWebsite from "../components/NavbarWebsite";
import NavbarFooter from "../components/Footer";
import useTranslation from "../hooks/useTranslation";

export const Route = createFileRoute("/pricing")({
  component: RouteComponent,
});

function RouteComponent() {
  const { translate } = useTranslation();

  return (
    <>
      <NavbarWebsite />

      <div className=" max-w-3xl mx-auto md:mt-16 mt-10">
        <p className="text-4xl font-extrabold text-center mb-4 pt-9 ">
          {translate("pricing_hero_title")}
        </p>
        <p className="text-l font-normal text-center mb-4 pb-2 px-4">
          {translate("pricing_hero_subtitle")}
        </p>
      </div>
      <div className="text-xs md:text-sm flex flex-row justify-center items-center gap-4 text-center pb-4 flex-wrap">
        <p className="rounded-full bg-gray-100 pt-2 pb-2 pl-3 pr-3">
          {translate("pricing_badge_trial")}
        </p>
        <p className="rounded-full bg-gray-100 pt-2 pb-2 pl-3 pr-3">
          {translate("pricing_badge_cancel_anytime")}
        </p>
      </div>

      <section className="w-full bg-white py-1 pb-10 md:py-6">
        <div className="mx-auto max-w-(--breakpoint-xl) px-4">
          <div className="flex flex-col md:flex-row md:justify-center md:items-stretch gap-6 flex-wrap">
            {/* Card 1: Starter */}
            <div className="w-full md:w-[380px] shrink-0 border bg-white border-slate-300 rounded-md text-left p-6 shadow-xs relative">
              <div className="absolute top-4 right-4 bg-green-400 text-white text-xs font-bold px-2 py-1 rounded-xs shadow-xs">
                {translate("pricing_card_trial_badge")}
              </div>
              <div className="font-bold text-xl mb-2">
                {translate("pricing_card_trial_title")}
              </div>
              <div className="text-gray-600 mb-4 md:h-[120px]">
                {translate("pricing_card_trial_description")}
              </div>
              <div className="flex align-baseline items-end">
                <div className="pt-5 font-extrabold text-4xl">FREE</div>
                <div className="pt-5 pl-2 font-medium text-xl">
                  {translate("pricing_card_trial_price_suffix")}
                </div>
              </div>
              <Link
                to="/free_trial_signup"
                className="w-full px-6 py-2 mt-5 text-gray-900 text-lg outline-solid outline-1 rounded-md flex items-center justify-center"
                style={{ backgroundColor: "#F0F8FF" }}
              >
                {"🚀 " + translate("pricing_card_trial_cta")}
              </Link>
              <ul className="pt-4 text-sm text-gray-700 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>{translate("pricing_card_trial_feature_texts")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>{translate("pricing_card_trial_feature_words")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>
                    {translate("pricing_card_trial_feature_audio_texts")}
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>
                    {translate("pricing_card_trial_feature_audio_words")}
                  </span>
                </li>
              </ul>
              <p className="pt-4 text-sm text-gray-700 space-y-">
                {translate("pricing_card_trial_footer")}
              </p>
            </div>

            {/* Card 2: Classic */}
            <div className="w-full md:w-[380px] shrink-0 border bg-white border-slate-300 rounded-md text-left p-6 shadow-xs relative">
              <div className="absolute top-4 right-4 bg-green-400 text-white text-xs font-bold px-2 py-1 rounded-xs shadow-xs">
                {translate("pricing_card_pro_badge")}
              </div>
              <div className="font-bold text-xl mb-2">
                {translate("pricing_card_pro_title")}
              </div>
              <div className="text-gray-600 mb-4 md:h-[120px]">
                {translate("pricing_card_pro_description")}
              </div>
              <div className="flex align-baseline items-end">
                <div className="pt-5 font-extrabold text-4xl">6,90 €</div>
                <div className="pt-5 pl-2 font-medium text-xl">
                  {translate("pricing_card_pro_price_suffix")}
                </div>
              </div>
              <Link
                to="/start-learning"
                className="w-full px-6 py-2 mt-5 text-white text-lg bg-gray-900 rounded-md flex items-center justify-center"
              >
                {translate("pricing_card_pro_cta")}
              </Link>
              <ul className="pt-4 text-sm text-gray-700 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>{translate("pricing_card_pro_feature_texts")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>{translate("pricing_card_pro_feature_words")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>{translate("pricing_card_pro_feature_audio_texts")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>{translate("pricing_card_pro_feature_audio_words")}</span>
                </li>
              </ul>
              <p className="pt-4 text-sm text-gray-700 space-y-">
                {translate("pricing_card_pro_footer")}
              </p>
            </div>

            {/* Card 3: Learn Together */}
            <div className="w-full md:w-[380px] shrink-0 border bg-white border-slate-300 rounded-md text-left p-6 shadow-xs relative">
              {/* Badge in the top-right corner */}
              <div className="absolute top-4 right-4 bg-green-400 text-white text-xs font-bold px-2 py-1 rounded-xs shadow-xs animate-pulse">
                {translate("pricing_card_duo_badge")}
              </div>

              <div className="font-bold text-xl mb-2">
                {translate("pricing_card_duo_title")}
              </div>
              <div className="text-gray-600 mb-4 md:h-[120px]">
                {translate("pricing_card_duo_description")}
              </div>

              <div className="flex align-baseline items-end">
                <div className="pt-5 font-extrabold text-4xl">9,80 €</div>
                <div className="pt-5 pl-2 font-medium text-xl">
                  {translate("pricing_card_duo_price_suffix")}
                </div>
              </div>

              <Link
                to="/start-learning"
                className="w-full px-6 py-2 mt-5 text-white text-lg bg-gray-900 rounded-md flex items-center justify-center"
              >
                {translate("pricing_card_duo_cta")}
              </Link>

              <ul className="pt-4 text-sm text-gray-700 space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>{translate("pricing_card_duo_feature_texts")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>{translate("pricing_card_duo_feature_words")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>{translate("pricing_card_duo_feature_audio_texts")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                  <span>
                    {translate("pricing_card_duo_feature_audio_words")}
                  </span>
                </li>
              </ul>
              <p className="pt-4 text-sm text-gray-700 space-y-">
                {translate("pricing_card_pro_footer")}
              </p>
            </div>
          </div>
        </div>
      </section>
      <NavbarFooter />
    </>
  );
}
